import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import dynamic from 'next/dynamic';
import { get, isEmpty, isNull } from 'lodash';
import { colors, maskTextStyles, fontSize } from '../../constants/styleConstants';
import PerformanceChips from '../PerformanceChips';
import {
  getDateFormat,
  isCustomProductionName,
  getGroupedProductionsDatesWithinRange,
  getGroupedProductionsDateForTheCard,
  getPerformanceDate,
  getArtistRoles,
  getRouteWithSlug,
  isFutureDatesExist,
  getCountryCity,
  isPastDate,
} from '../../utils/common';
import { useTranslation } from '../../src/i18n';
import ProductionChip from '../ProductionChip';
import ProductionMaskIcon from '../ProductionMaskIcon';
import NextLink from '../NextLink';
import { route } from '../../constants/routes';

// dynamic imports
const Title = dynamic(() => import('../Title'));

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginLeft: 11,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 8,
    },
  },
  composer: {
    fontStyle: 'italic',
    color: colors.grey3,
    fontSize: fontSize.medium,
    marginRight: '0 !important',
  },
  workName: {
    fontSize: fontSize.medium,
    color: colors.grey3,
    opacity: '0.7',
  },
  link: {
    color: colors.blueChip,
  },
  venue: {
    color: colors.grey2,
    '& span': {
      fontSize: fontSize.medium,
      lineHeight: 1.57,
    },
  },
  address: {
    color: colors.grey2,
    fontSize: fontSize.medium,
    lineHeight: 1.57,
  },
  availability: {
    fontSize: fontSize.medium,
    color: colors.grey1,
  },
  maskCompanyText: {
    textShadow: props => (!props.expanded ? '0 0 5px rgb(0, 103, 197, 0.4)' : '0 0 5px rgb(111, 111, 111, 0.4)'),
    ...maskTextStyles,
  },
  maskText: {
    textShadow: '0 0 5px rgb(111, 111, 111, 0.4)',
    ...maskTextStyles,
  },

  summaryBlock: {
    alignItems: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  summaryRow: {
    flex: 1,
  },
  summaryInfo: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    '& span': {
      textDecoration: props => props.cancelled && 'line-through',
    },
    '& span:last-child': {
      wordBreak: 'break-word',
    },
  },
  workInfo: {
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
  },
  boldTitle: {
    fontSize: fontSize.basic,
    color: colors.darkText,
    fontWeight: 'bold',
  },
  lightTitle: {
    fontSize: fontSize.basic,
    color: colors.lightTitle,
    fontWeight: 'bold',
  },
  nextRow: {
    flexBasis: '100%',
  },
  liveStream: {
    display: 'none',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  cancelledChip: {
    float: 'right',
    paddingTop: 7,
  },
  boxOffice: {
    color: colors.primary,
    textTransform: 'uppercase',
    fontSize: fontSize.smallTitle,
    fontWeight: 'bold',
    lineHeight: 1.33,
    letterSpacing: '1px',
    paddingTop: 7,
  },
  performanceChips: {
    marginLeft: 5,
  },
  artistInfoSec: {
    borderTop: `solid 1px ${colors.contentDivider}`,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginTop: 13,
    paddingTop: 10,
  },
  artistRole: {
    fontSize: fontSize.smallTitle,
    fontWeight: 'bold',
    color: colors.grey3,
    lineHeight: 'normal',
    paddingTop: 2,
  },
  artistProfession: {
    fontSize: fontSize.smallTitle,
    fontStyle: 'italic',
    color: colors.grey3,
  },
  professionHeading: {
    fontSize: fontSize.smallTitle,
    color: colors.grey1,
  },
  profile: {
    fontSize: fontSize.smallTitle,
    color: colors.grey1,
  },
  artistInfo: {
    '& a': {
      fontSize: fontSize.smallTitle,
      color: colors.blueChip,
    },
  },
  prodTitle: {
    fontSize: fontSize.medium,
    color: colors.grey2,
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  perfTitle: {
    fontSize: fontSize.basic,
    color: colors.darkText,
    fontWeight: 'bold',
  },
  pastDate: {
    opacity: 0.5,
  },
}));

const WorkInfoBlock = ({ work, composer, isMultiWorks, production }) => {
  const classes = useStyles();
  const name = work?.original_name || work?.name || '';

  return (
    <span className={classnames(classes.workInfo, { [classes.nextRow]: isMultiWorks })}>
      <span className={isNull(production) ? classes.boldTitle: classes.lightTitle}>
        {name}
      </span>
      {work?.name && work?.name !== name && <span className={classes.workName}>{`\u00A0(${work?.name})`}</span>}
      {composer?.name && <span className={classes.composer}>{`, ${composer?.name}`}</span>}
    </span>
  );
};

const TextLink = ({ list, expanded, fieldToDisplay, onClick }) => {
  const classes = useStyles();
  return list.map(item => (
    <span
      key={`${get(item, fieldToDisplay)}-${get(item, 'id')}`}
      onClick={e => onClick(e, item)}
      className={classnames(expanded ? classes.link : classes.venue, item?.id === -1 && classes.maskCompanyText)}
    >
      &nbsp; {list.length === 1 && ` | `}
      {get(item, fieldToDisplay)} | &nbsp;
    </span>
  ));
};

const ArtistInfo = ({ production, profile }) => {
  const classes = useStyles();
  const artistDetails = getArtistRoles(production, profile);
  const conductor = production?.crew.find(o => o?.profession?.id === 4);
  const stageDirector = production?.crew.find(o => o?.profession?.id === 2);
  return (
    <div className={classes.artistInfoSec}>
      <ProductionMaskIcon addedBy={production?.addedBy} className={classes.iconPadding} />
      <div className={classes.artistRole}>{artistDetails?.roles}</div>
      {artistDetails?.type && <div className={classes.artistProfession}>&nbsp;({artistDetails?.type})</div>}

      {!isEmpty(conductor) && (
        <div className={classes.artistInfo}>
          <span className={classes.professionHeading}> &nbsp; | {conductor?.profession?.name}: </span>
          {conductor.profile && !conductor.profile.is_hidden ? (
            <NextLink
              href={`${route.ARTISTS}/[id]/[[...action]]`}
              as={getRouteWithSlug(route.ARTISTS, get(conductor, 'profile'))}
              target="_blank"
            >
              {conductor?.profile?.name}
            </NextLink>
          ) : (
            <span className={classes.profile}>{conductor?.profileReference}</span>
          )}
        </div>
      )}
      {!isEmpty(stageDirector) && (
        <div className={classes.artistInfo}>
          <span className={classes.professionHeading}> &nbsp;| {stageDirector?.profession?.name}: </span>
          {stageDirector.profile && !stageDirector.profile.is_hidden ? (
            <NextLink
              href={`${route.ARTISTS}/[id]/[[...action]]`}
              as={getRouteWithSlug(route.ARTISTS, get(stageDirector, 'profile'))}
              target="_blank"
            >
              {stageDirector?.profile?.name}
            </NextLink>
          ) : (
            <span className={classes.profile}>{stageDirector?.profileReference}</span>
          )}
        </div>
      )}
    </div>
  );
};

const ProductionAccordionSummary = ({
  production,
  isExpanded,
  viewCompanyDetails,
  viewType = 'production',
  cancelled,
  range,
  page,
  profile,
}) => {
  const expanded = isExpanded === production?.id;
  const classes = useStyles({ expanded, cancelled });
  const country = get(production, 'country', {});
  const city = get(production, 'city', {});
  const maskCityCountry = city?.id === -1 || country?.id === -1;
  const { t } = useTranslation();
  const matinee = production?.performanceTypes?.filter(p => p.slug === 'matinee').length > 0;
  const tour = production?.performanceTypes?.filter(p => p.slug === 'tour').length > 0;
  let showTickets = false;

  // to get the coproductions from companies
  const coProductions = production?.companies?.filter(c => c?.is_partner && c?.id !== production?.company.id);

  const grouppedDates = range
    ? getGroupedProductionsDatesWithinRange(production, range)
    : getGroupedProductionsDateForTheCard(production, true);

  const lengthOfGroupedDates = Object.keys(grouppedDates).length;
  // set showTickets
  if (viewType === 'production') {
    showTickets = !cancelled && lengthOfGroupedDates > 0 && production?.boxOfficeWebsite !== null;
  } else {
    // check if the performance date is in future
    showTickets = !cancelled && isFutureDatesExist([production]) && production?.boxOfficeWebsite !== null;
  }

  const getSummaryInfo = (
    <div className={classes.summaryInfo}>
      {isCustomProductionName(viewType === 'production' ? production : production?.production) && (
        <Title size="big" weight="regular">
          {viewType === 'production' ? `${production?.name}` : `${production?.production?.name}`} &nbsp;
        </Title>
      )}
      <WorkInfoBlock
        work={production?.work}
        production={production?.production?.name}
        composer={viewType === 'production' ? production?.work?.composer : production?.composer}
        isMultiWorks={(production?.works || [])?.length > 0}
      />
      {(production?.works || []).map(work => (
        <WorkInfoBlock
          key={`${work.id}-${work.name}`}
          production={production?.production?.name}
          work={work}
          composer={work?.composer}
          isMultiWorks={(production?.works || [])?.length > 0}
        />
      ))}
      {production?.companies?.length > 0 && (
        <TextLink
          list={production?.companies}
          onClick={(e, item) => {
            if (expanded && item?.id !== -1) {
              e.stopPropagation();
              viewCompanyDetails(item);
            }
          }}
          expanded={expanded}
          fieldToDisplay="name"
        />
      )}
      {production?.festivals?.length > 0 && (
        <TextLink
          list={production?.festivals}
          onClick={(e, item) => {
            if (expanded && item?.id !== -1) {
              e.stopPropagation();
              viewCompanyDetails(item);
            }
          }}
          expanded={expanded}
          fieldToDisplay="name"
        />
      )}
      <span className={classnames(classes.address, classes.fontsize, maskCityCountry && classes.maskText)}>
        {production?.festivals?.length <= 0 && production?.companies?.length <= 0 && <span>&nbsp; | &nbsp;</span>}
        {getCountryCity(production)} &nbsp;
      </span>
      {production?.dates?.length && viewType === 'production' && (
        <span className={classnames(classes.address, classes.fontsize)}>
          | {t('operabase.general.FN_PERFORMANCES')}{' '}
          <span style={{ fontWeight: 'bold' }}>({production?.dates?.length})</span>
        </span>
      )}
      {!expanded && production?.performanceTypes?.length > 0 && (
        <PerformanceChips
          type="small"
          performanceTypes={production?.performanceTypes}
          className={classes.performanceChips}
          isCoProduced={coProductions?.length > 0}
        />
      )}
    </div>
  );

  return (
    <Grid container className={classnames(classes.root)}>
      <Grid container className={classnames(classes.summaryBlock)}>
        <Grid item sm={2} lg={2}>
          <span
            className={classnames({
              [classes.prodTitle]: viewType === 'production',
              [classes.perfTitle]: viewType === 'performance',
              [classes.pastDate]: viewType === 'performance' && isPastDate(production?.date),
            })}
          >
            {viewType === 'production'
              ? getDateFormat(production?.minDate, production?.maxDate)
              : page
              ? getPerformanceDate(production?.date, 'ddd DD MMM YYYY')
              : getPerformanceDate(production?.date, 'ddd DD MMM')}
          </span>
          {viewType === 'performance' && (matinee || tour) && (
            <span style={{ fontStyle: 'italic' }}>
              {matinee && 'm'} {tour && 't'}
            </span>
          )}
        </Grid>
        <Grid item className={classes.summaryRow}>
          {!expanded && !cancelled && showTickets && (
            <span className={classes.boxOffice} style={{ float: 'right' }}>
              {t('operabase.general.FN_TICKETS')}
            </span>
          )}
          {cancelled && <ProductionChip chipType="cancelled" className={classes.cancelledChip} />}
          {production?.isArchived && <ProductionChip chipType="archived" className={classes.cancelledChip} />}

          {getSummaryInfo}
          {page === 'artist' && <ArtistInfo expanded={expanded} production={production} profile={profile} />}
        </Grid>
      </Grid>
      {!expanded && !cancelled && (
        <div className={classes.liveStream}>
          <span className={classes.availability}>Available online until Friday, 31 July 2020 17:00 PST &nbsp;</span>
          <span className={classes.boxOffice}>{t('operabase.general.FN_FULL_PRODUCTION_VIDEO')}</span>
        </div>
      )}
    </Grid>
  );
};

ProductionAccordionSummary.propTypes = {
  production: PropTypes.object,
  viewCompanyDetails: PropTypes.func,
  viewType: PropTypes.string,
  cancelled: PropTypes.bool,
  range: PropTypes.string,
};

export default ProductionAccordionSummary;
